import moment from 'moment';

export const GRADIENT_THEMES = {
    today: {
        gradient: 'linear-gradient(135deg, #b45309 0%, #f59e0b 55%, #fbbf24 100%)',
        shadow: '0 8px 20px rgba(245, 158, 11, 0.28)',
    },
    blue: {
        gradient: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 55%, #60a5fa 100%)',
        shadow: '0 8px 20px rgba(37, 99, 235, 0.24)',
    },
    purple: {
        gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 55%, #a78bfa 100%)',
        shadow: '0 8px 20px rgba(124, 58, 237, 0.24)',
    },
    teal: {
        gradient: 'linear-gradient(135deg, #0f766e 0%, #0d9488 55%, #2dd4bf 100%)',
        shadow: '0 8px 20px rgba(13, 148, 136, 0.24)',
    },
    red: {
        gradient: 'linear-gradient(135deg, #991b1b 0%, #dc2626 55%, #f87171 100%)',
        shadow: '0 8px 20px rgba(220, 38, 38, 0.28)',
    },
};

export const buildGradientCard = (label, value, theme = 'blue') => ({
    label,
    value: String(value),
    ...GRADIENT_THEMES[theme],
});

/** Cancelled summary card: main value + "N cancelled" subtext. */
export const buildCancelledSummaryCard = (cancelledRecords, options = {}) => {
    const rows = cancelledRecords || [];
    const {
        label = 'Cancelled',
        valueField = null,
        valueFormatter = formatQty,
        entityLabel = 'cancelled',
        alwaysShow = true,
    } = options;
    if (!alwaysShow && !rows.length) {
        return null;
    }
    const value = valueField
        ? valueFormatter(sumField(rows, valueField))
        : String(rows.length);
    return {
        ...buildGradientCard(label, value, 'red'),
        subValue: `${rows.length} ${entityLabel}`,
    };
};

export const isCancelledSummaryCard = (card) => {
    const label = String(card?.label || '').trim().toLowerCase();
    return label === 'cancelled' || label.startsWith('cancelled ');
};

export const sumField = (records, field) =>
    (records || []).reduce((sum, row) => sum + (Number(row[field]) || 0), 0);

export const formatQty = (value) => Number(value || 0).toFixed(2);

export const formatAmount = (value) => Number(value || 0).toFixed(2);

const toCaps = (value) => String(value || '').trim().toUpperCase();

export const isInactiveRecord = (row) => {
    if (row?.is_inactive === true) {
        return true;
    }
    if (row?.is_inactive === false) {
        return false;
    }
    const candidates = [
        row?.status,
        row?.status_2,
        row?.customer_status,
        row?.cus_dc_status,
        row?.customer_dc_status,
        row?.dc_invioce,
    ];
    return candidates.some((status) => {
        if (status === undefined || status === null || status === '') {
            return false;
        }
        if (typeof status === 'boolean') {
            return !status;
        }
        const normalized = String(status).trim().toLowerCase().replace(/\s+/g, ' ');
        return (
            normalized === 'inactive'
            || normalized === 'cancelled'
            || normalized === 'false'
        );
    });
};

export const splitRecordsByStatus = (records) => {
    const active = [];
    const cancelled = [];

    (records || []).forEach((row) => {
        if (isInactiveRecord(row)) {
            cancelled.push(row);
        } else {
            active.push(row);
        }
    });

    return { active, cancelled };
};

export const buildQtySummaryStat = (label, field, records, options = {}) => {
    const { active, cancelled } = splitRecordsByStatus(records);
    const stats = [{
        label,
        value: formatQty(sumField(active, field)),
        accent: options.accent,
    }];

    if (options.includeCancelledQty && cancelled.length) {
        stats.push({
            label: options.cancelledLabel || `Cancelled ${label}`,
            value: formatQty(sumField(cancelled, field)),
            accent: 'cancelled',
            isInactiveQty: true,
        });
    }

    return stats;
};

export const flattenQtySummaryStats = (...statGroups) => statGroups.flat();

export const formatRecordStatus = (isActive) => (isActive ? 'Active' : 'Cancelled');

export const normalizeListDate = (value) => {
    if (!value) {
        return '';
    }
    const parsed = moment(value, ['DD-MM-YYYY', 'D-M-YYYY', 'DD/MM/YYYY', 'D/M/YYYY'], true);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : String(value).trim();
};

export const isSameListDate = (left, right) => normalizeListDate(left) === normalizeListDate(right);

export const buildDcSummaryRows = (records) => {
    const groups = new Map();

    (records || []).forEach((row) => {
        const customer = row.customer_name || '-';
        const site = row.consignee_projectname || '-';
        const grade = row.grade || '-';
        const key = `${customer}|${site}|${grade}`;
        const dcQty = Number(row.dc_qty) || 0;
        const rate = Number(row.rate) || 0;

        if (!groups.has(key)) {
            groups.set(key, {
                customer,
                site,
                grade,
                dcQty: 0,
                dcCount: 0,
                amount: 0,
                rate,
            });
        }

        const group = groups.get(key);
        group.dcQty += dcQty;
        group.dcCount += 1;
        group.amount += dcQty * rate;
    });

    return Array.from(groups.values())
        .map((group) => ({
            ...group,
            rate: group.dcQty > 0 ? (group.amount / group.dcQty) : group.rate,
        }))
        .sort((left, right) => (
            left.customer.localeCompare(right.customer)
            || left.site.localeCompare(right.site)
            || left.grade.localeCompare(right.grade)
        ));
};

/** @deprecated Use buildDcSummaryRows */
export const buildTodayDcSummaryRows = buildDcSummaryRows;

/** Aggregated QC delivery challan report rows (not individual DCs). */
export const buildQcDeliveryChallanReportRows = (records) => {
    const groups = new Map();

    (records || []).forEach((row) => {
        if (row.is_inactive) {
            return;
        }
        const date = row.delivery_challan_date || '-';
        const customer = row.customer_name || '-';
        const site = row.consignee_projectname || '-';
        const unit = row.unit || row.grade || '-';
        const deliveryMode = row.delivery_mode || '-';
        const rate = Number(row.rate) || 0;
        const key = `${date}|${customer}|${site}|${unit}|${deliveryMode}|${rate}`;
        const dcQty = Number(row.dc_qty) || 0;

        if (!groups.has(key)) {
            groups.set(key, {
                delivery_challan_date: date,
                customer_name: customer,
                site,
                unit,
                delivery_mode: deliveryMode,
                rate,
                dc_qty: 0,
                amount: 0,
                detail_rows: [],
            });
        }

        const group = groups.get(key);
        group.dc_qty += dcQty;
        group.amount += dcQty * rate;
        group.detail_rows.push({
            delivery_challan_no: row.delivery_challan_no || '-',
            delivery_challan_time: row.delivery_challan_time || '-',
            grade: row.grade || unit,
            dc_qty: dcQty,
            dc_qty_display: formatQty(dcQty),
            rate_display: formatAmount(rate),
            amount_display: formatAmount(dcQty * rate),
            vehicle_no: row.customer_vehicle_no || row.vehicle_name || '-',
            plant: row.plant || '-',
        });
    });

    return Array.from(groups.values())
        .map((group, index) => ({
            id: index + 1,
            ...group,
            customer_name: toCaps(group.customer_name),
            site: toCaps(group.site),
            unit: toCaps(group.unit),
            delivery_mode: toCaps(group.delivery_mode),
            detail_rows: (group.detail_rows || []).map((detail) => ({
                ...detail,
                grade: toCaps(detail.grade),
                vehicle_no: toCaps(detail.vehicle_no),
                plant: toCaps(detail.plant),
            })),
            dc_qty_display: formatQty(group.dc_qty),
            rate_display: formatAmount(group.rate),
            amount_display: formatAmount(group.amount),
        }))
        .sort((left, right) => (
            String(right.delivery_challan_date).localeCompare(String(left.delivery_challan_date))
            || left.customer_name.localeCompare(right.customer_name)
            || left.site.localeCompare(right.site)
            || left.unit.localeCompare(right.unit)
        ));
};
